import { ChevronDownIcon } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SELECT_TRIGGER_CLASS } from "@/components/ui/select";
import { cn } from "@/lib/utils";

/** One choice of a `<MultiSelect>`: its value and what it says. */
export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: readonly MultiSelectOption[];
  /** The chosen values; none means every option. */
  value: readonly string[];
  /** The new choice, in the options' order. */
  onChange: (value: string[]) => void;
  /** What the button says about the choice, such as "Alle groepen" or "Klanten, Kranen". */
  label: string;
  /** What is chosen, for screen readers: "Groep". */
  name: string;
  /** The first item, ticked while nothing is chosen, which clears the choice: "Alle groepen". */
  allLabel: string;
  size?: "sm" | "default";
  className?: string;
}

/**
 * A multiple-choice dropdown in the dashboard's theme: a button like
 * `<SelectTrigger>` that opens a list of checkboxes, with "all" on top. The
 * list stays open while ticking. Keyboard: Enter, Space or Arrow Down opens
 * it, the arrows move, Space or Enter ticks, typing jumps to a match, Escape
 * closes. The button's text truncates, so its width never follows the choice.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  label,
  name,
  allLabel,
  size = "default",
  className,
}: MultiSelectProps) {
  const chosen = new Set(value);
  const toggle = (option: string, on: boolean) =>
    onChange(
      options.filter((o) => (o.value === option ? on : chosen.has(o.value))).map((o) => o.value),
    );

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger data-size={size} className={cn(SELECT_TRIGGER_CLASS, className)}>
        <span className="min-w-0 truncate">
          <span className="sr-only">{name}: </span>
          {label}
        </span>
        <ChevronDownIcon aria-hidden="true" className="text-muted size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-80 min-w-(--radix-dropdown-menu-trigger-width)"
      >
        <DropdownMenuCheckboxItem
          checked={value.length === 0}
          onCheckedChange={() => onChange([])}
          onSelect={(e) => e.preventDefault()}
        >
          {allLabel}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={chosen.has(option.value)}
            onCheckedChange={(on) => toggle(option.value, on)}
            onSelect={(e) => e.preventDefault()}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
